import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import employeesApi from 'Api/axios/employees-api';
import useTranslation from 'Hooks/useTranslation';

const DEBOUNCE_MS = 350;

/**
 * Server-side searchable user picker. Owns its own search/debounce/loading;
 * exposes only the chosen user (or `null`) through `onChange`.
 *
 * SRP: this component knows how to look up users. Forms that need to embed a
 * user picker compose it without duplicating the search plumbing.
 */
export default function UserAutocomplete({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  disablePortal,
}) {
  const { t2 } = useTranslation('EditCourseSessions');
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [users, setUsers] = useState(value ? [value] : []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const fetchUsers = useCallback(async term => {
    setLoading(true);
    const response = await employeesApi.getUsers({
      name: term || undefined,
      pageSize: 20,
      status: 'true',
    });
    if (!response.error) setUsers(response.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers(debounced);
  }, [debounced, fetchUsers]);

  return (
    <Autocomplete
      disablePortal={disablePortal}
      value={value || null}
      onChange={(_, next) => onChange(next || null)}
      onInputChange={(_, next, reason) => {
        if (reason === 'input') setInput(next);
      }}
      options={users}
      loading={loading}
      disabled={disabled}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      getOptionLabel={option => option.fullName || ''}
      noOptionsText={t2('empty.noAttendees')}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

UserAutocomplete.propTypes = {
  value: PropTypes.shape({ id: PropTypes.string, fullName: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  disablePortal: PropTypes.bool,
};

UserAutocomplete.defaultProps = {
  value: null,
  label: '',
  placeholder: '',
  error: false,
  helperText: '',
  disabled: false,
  disablePortal: false,
};
